import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  CheckCircle2,
  Columns3,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Plus as PlusIcon,
  RefreshCw,
  Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PracticeCountPill, PracticeShareRing } from '../components/pf/PfUsageMetrics';
import api from '../services/api';

const labelRighePerPagina = 'Righe per pagina:';

const labelDaA = ({ from, to, count }) => {
  const total = count !== -1 ? count : `più di ${to}`;
  return `${from}-${to} di ${total}`;
};

function normalizeBadgeColor(value) {
  return String(value || '').trim().toLowerCase();
}

function isHexColor(value) {
  const s = String(value || '').trim();
  return /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(s);
}

function hexToRgb(hex) {
  const raw = String(hex || '').trim().replace('#', '');
  if (raw.length === 3) {
    const r = parseInt(raw[0] + raw[0], 16);
    const g = parseInt(raw[1] + raw[1], 16);
    const b = parseInt(raw[2] + raw[2], 16);
    return { r, g, b };
  }
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  return { r, g, b };
}

function rgba(rgb, alpha) {
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

function getBadgeStyle(color) {
  const c = normalizeBadgeColor(color);
  if (isHexColor(c)) {
    const rgb = hexToRgb(c);
    return {
      border: rgba(rgb, 0.35),
      bg: rgba(rgb, 0.06),
      fg: c
    };
  }
  const map = {
    blue: { border: 'rgba(59, 130, 246, 0.35)', bg: 'rgba(59, 130, 246, 0.06)', fg: '#2563EB' },
    amber: { border: 'rgba(245, 158, 11, 0.35)', bg: 'rgba(245, 158, 11, 0.06)', fg: '#D97706' },
    green: { border: 'rgba(16, 185, 129, 0.35)', bg: 'rgba(16, 185, 129, 0.06)', fg: '#059669' },
    teal: { border: 'rgba(20, 184, 166, 0.35)', bg: 'rgba(20, 184, 166, 0.06)', fg: '#0D9488' },
    red: { border: 'rgba(239, 68, 68, 0.35)', bg: 'rgba(239, 68, 68, 0.06)', fg: '#DC2626' },
    gray: { border: 'rgba(100, 116, 139, 0.35)', bg: 'rgba(100, 116, 139, 0.06)', fg: '#475569' },
    grey: { border: 'rgba(100, 116, 139, 0.35)', bg: 'rgba(100, 116, 139, 0.06)', fg: '#475569' }
  };
  return map[c] || map.gray;
}

export default function PfStatiPratica() {
  const badgeColorPresets = useMemo(
    () => [
      { value: 'blue', label: 'Blue', hex: '#2563EB' },
      { value: 'amber', label: 'Amber', hex: '#D97706' },
      { value: 'green', label: 'Green', hex: '#059669' },
      { value: 'teal', label: 'Teal', hex: '#0D9488' },
      { value: 'red', label: 'Red', hex: '#DC2626' },
      { value: 'gray', label: 'Gray', hex: '#475569' }
    ],
    []
  );

  const columnDefs = useMemo(
    () => [
      { key: 'codice', label: 'Codice', sortable: true, align: 'left' },
      { key: 'nome_visualizzato', label: 'Nome', sortable: true, align: 'left' },
      { key: 'colore_badge', label: 'Badge', sortable: true, align: 'left' },
      { key: 'ordinamento', label: 'Ordine', sortable: true, align: 'left' },
      { key: 'finale', label: 'Finale', sortable: true, align: 'left' },
      { key: 'totale_pratiche_collegate', label: 'Totale pratiche', sortable: true, align: 'right' },
      { key: 'percentuale_sul_totale', label: '% sul totale', sortable: true, align: 'right' }
    ],
    []
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('ordinamento');
  const [filterText, setFilterText] = useState('');
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState([
    'nome_visualizzato',
    'colore_badge',
    'ordinamento',
    'finale',
    'totale_pratiche_collegate',
    'percentuale_sul_totale'
  ]);

  const [openDialog, setOpenDialog] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [formData, setFormData] = useState({
    codice: '',
    nome_visualizzato: '',
    colore_badge: 'gray',
    ordinamento: 0,
    finale: false
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const badgeColorSelectValue = useMemo(() => {
    const current = normalizeBadgeColor(formData.colore_badge);
    const presets = badgeColorPresets.map((p) => p.value);
    if (presets.includes(current)) return current;
    return 'custom';
  }, [badgeColorPresets, formData.colore_badge]);

  const badgeColorPickerValue = useMemo(() => {
    const current = String(formData.colore_badge || '').trim();
    if (isHexColor(current)) return current;
    const preset = badgeColorPresets.find((p) => p.value === normalizeBadgeColor(current));
    return preset?.hex || '#475569';
  }, [badgeColorPresets, formData.colore_badge]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const response = await api.get('/pf-stati-pratica');
      setRows(response.data || []);
      setError('');
    } catch (err) {
      console.error('Error fetching pf stati pratica:', err);
      setError('Errore nel caricamento degli stati pratica');
      toast.error('Errore nel caricamento degli stati pratica');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const displayedColumns = useMemo(() => columnDefs.filter((c) => visibleColumns.includes(c.key)), [columnDefs, visibleColumns]);

  const filteredRows = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const haystack = [r.codice, r.nome_visualizzato, r.colore_badge].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, filterText]);

  const sortedRows = useMemo(() => {
    const stabilized = filteredRows.map((el, index) => [el, index]);
    const isAsc = order === 'asc';
    stabilized.sort((a, b) => {
      const rowA = a[0];
      const rowB = b[0];
      const valA = rowA?.[orderBy];
      const valB = rowB?.[orderBy];

      if (
        orderBy === 'finale' ||
        orderBy === 'id' ||
        orderBy === 'ordinamento' ||
        orderBy === 'totale_pratiche_collegate' ||
        orderBy === 'percentuale_sul_totale'
      ) {
        const numA = Number(valA || 0);
        const numB = Number(valB || 0);
        return isAsc ? numA - numB : numB - numA;
      }

      const strA = (valA || '').toString().toLowerCase();
      const strB = (valB || '').toString().toLowerCase();
      if (strA < strB) return isAsc ? -1 : 1;
      if (strA > strB) return isAsc ? 1 : -1;
      return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
  }, [filteredRows, order, orderBy]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (e) => {
    setFilterText(e.target.value);
    setPage(0);
  };

  const handleToggleColumn = (key) => {
    setVisibleColumns((prev) => {
      if (prev.includes(key)) {
        if (prev.length === 1) return prev;
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  };

  const handleOpenDialog = (row = null) => {
    if (row) {
      setCurrentRow(row);
      setFormData({
        codice: row.codice || '',
        nome_visualizzato: row.nome_visualizzato || '',
        colore_badge: row.colore_badge || 'gray',
        ordinamento: Number(row.ordinamento || 0),
        finale: Boolean(row.finale)
      });
    } else {
      setCurrentRow(null);
      setFormData({
        codice: '',
        nome_visualizzato: '',
        colore_badge: 'gray',
        ordinamento: 0,
        finale: false
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentRow(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'ordinamento' ? Number(value) : value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSubmit = async () => {
    try {
      if (currentRow) {
        await api.put(`/pf-stati-pratica/${currentRow.id}`, formData);
        toast.success('Stato pratica aggiornato con successo');
      } else {
        await api.post('/pf-stati-pratica', formData);
        toast.success('Stato pratica creato con successo');
      }
      handleCloseDialog();
      fetchRows();
    } catch (err) {
      console.error('Error saving pf stato pratica:', err);
      toast.error(err.response?.data?.message || 'Errore nel salvataggio');
    }
  };

  const openDeleteDialog = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/pf-stati-pratica/${deleteTarget.id}`);
      toast.success('Stato pratica eliminato con successo');
      closeDeleteDialog();
      fetchRows();
    } catch (err) {
      console.error('Error deleting pf stato pratica:', err);
      toast.error("Errore durante l'eliminazione");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50">
            <CheckCircle2 className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <Typography variant="h4" className="font-bold text-gray-800 dark:text-gray-100">
              Parametri — Stati Pratica
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Anagrafica degli stati del workflow delle Pratiche Finanziarie
            </Typography>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchRows} title="Ricarica">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={() => handleOpenDialog()}>
            <PlusIcon className="mr-2 h-4 w-4" />
            Nuovo Stato
          </Button>
        </div>
      </div>

      {error && (
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
      )}

      <Menu anchorEl={columnsMenuAnchor} open={Boolean(columnsMenuAnchor)} onClose={() => setColumnsMenuAnchor(null)}>
        {columnDefs.map((column) => {
          const checked = visibleColumns.includes(column.key);
          const disableHide = checked && visibleColumns.length === 1;
          return (
            <MenuItem key={column.key} dense onClick={() => handleToggleColumn(column.key)} disabled={disableHide}>
              <Checkbox size="small" checked={checked} />
              <ListItemText primary={column.label} />
            </MenuItem>
          );
        })}
      </Menu>

      <Card className="shadow-sm border-none overflow-hidden rounded-xl">
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={filterText}
            onChange={handleFilterChange}
            placeholder="Cerca per codice o nome..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              )
            }}
            sx={{ maxWidth: 520 }}
          />
          <Tooltip title="Colonne">
            <IconButton onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}>
              <Columns3 size={18} />
            </IconButton>
          </Tooltip>
        </Box>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'background.neutral' }}>
              <TableRow>
                {displayedColumns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.align}
                    sx={column.key === 'totale_pratiche_collegate' ? { minWidth: 104, whiteSpace: 'nowrap' } : column.key === 'percentuale_sul_totale' ? { minWidth: 88, whiteSpace: 'nowrap' } : undefined}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={orderBy === column.key}
                        direction={orderBy === column.key ? order : 'asc'}
                        onClick={() => handleRequestSort(column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={displayedColumns.length + 1} align="center" className="py-8">
                    <RefreshCw className="mx-auto h-6 w-6 animate-spin text-gray-400" />
                  </TableCell>
                </TableRow>
              ) : sortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={displayedColumns.length + 1} align="center" className="py-8 text-gray-500">
                    Nessuno stato pratica trovato
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow key={row.id} hover>
                      {displayedColumns.map((column) => (
                        <TableCell
                          key={`${row.id}-${column.key}`}
                          align={column.align}
                          className={column.key === 'nome_visualizzato' ? 'font-medium' : undefined}
                          sx={column.key === 'totale_pratiche_collegate' ? { minWidth: 104 } : column.key === 'percentuale_sul_totale' ? { minWidth: 88 } : undefined}
                        >
                          {column.key === 'finale' ? (
                            <Checkbox checked={Boolean(row.finale)} disabled />
                          ) : column.key === 'colore_badge' ? (
                            (() => {
                              const style = getBadgeStyle(row.colore_badge);
                              return (
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    border: `1px solid ${style.border}`,
                                    backgroundColor: style.bg,
                                    minWidth: 44
                                  }}
                                >
                                  <Box sx={{ width: 14, height: 14, borderRadius: 99, backgroundColor: style.fg }} />
                                </Box>
                              );
                            })()
                          ) : column.key === 'totale_pratiche_collegate' ? (
                            <PracticeCountPill value={row.totale_pratiche_collegate} />
                          ) : column.key === 'percentuale_sul_totale' ? (
                            <PracticeShareRing value={row.percentuale_sul_totale} />
                          ) : (
                            row[column.key] ?? '-'
                          )}
                        </TableCell>
                      ))}
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                          <IconButton size="small" onClick={() => handleOpenDialog(row)} color="primary">
                            <EditIcon size={18} />
                          </IconButton>
                          <IconButton size="small" onClick={() => openDeleteDialog(row)} color="error">
                            <DeleteIcon size={18} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={sortedRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={labelRighePerPagina}
          labelDisplayedRows={labelDaA}
        />
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{currentRow ? 'Modifica Stato Pratica' : 'Nuovo Stato Pratica'}</DialogTitle>
        <DialogContent dividers className="space-y-4">
          <TextField
            autoFocus
            margin="dense"
            name="codice"
            label="Codice (univoco)"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.codice}
            onChange={handleChange}
            helperText="Es: inviata, in_valutazione, approvata..."
          />
          <TextField
            margin="dense"
            name="nome_visualizzato"
            label="Nome visualizzato"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.nome_visualizzato}
            onChange={handleChange}
          />
          <TextField
            margin="dense"
            name="colore_badge"
            label="Colore badge"
            select
            fullWidth
            variant="outlined"
            value={badgeColorSelectValue}
            onChange={(e) => {
              const value = String(e.target.value || '');
              if (value === 'custom') {
                setFormData((prev) => ({ ...prev, colore_badge: prev.colore_badge || '#475569' }));
                return;
              }
              setFormData((prev) => ({ ...prev, colore_badge: value }));
            }}
            helperText="Seleziona un colore predefinito oppure usa il selettore per un colore personalizzato"
          >
            {badgeColorPresets.map((preset) => (
              <MenuItem key={preset.value} value={preset.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: 99,
                      backgroundColor: preset.hex,
                      border: `1px solid ${rgba(hexToRgb(preset.hex), 0.35)}`
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {preset.label}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
            <MenuItem value="custom">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Personalizzato
              </Typography>
            </MenuItem>
          </TextField>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TextField
              margin="dense"
              label="Colore personalizzato"
              type="color"
              value={badgeColorPickerValue}
              onChange={(e) => {
                const value = String(e.target.value || '').trim();
                if (!value) return;
                setFormData((prev) => ({ ...prev, colore_badge: value }));
              }}
              sx={{ width: 160 }}
              InputLabelProps={{ shrink: true }}
            />
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                border: `1px solid ${getBadgeStyle(formData.colore_badge).border}`,
                backgroundColor: getBadgeStyle(formData.colore_badge).bg
              }}
            />
          </Box>
          <TextField
            margin="dense"
            name="ordinamento"
            label="Ordine di visualizzazione"
            type="number"
            fullWidth
            variant="outlined"
            value={formData.ordinamento}
            onChange={handleChange}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox name="finale" checked={Boolean(formData.finale)} onChange={handleCheckboxChange} />
            <Typography>Finale (stato terminale)</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={handleCloseDialog}>
            Annulla
          </Button>
          <Button onClick={handleSubmit}>Salva</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Vuoi eliminare lo stato pratica <strong>{deleteTarget?.nome_visualizzato || deleteTarget?.codice || ''}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={closeDeleteDialog} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button onClick={handleDeleteConfirm} disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
